import { DatePipe, AsyncPipe, NgClass, SlicePipe, TitleCasePipe } from '@angular/common';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Renderer2,
  effect,
  input,
  output,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
  Validators,
  FormControl,
} from '@angular/forms';
import { AppErrors } from '@fitness-force/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { PopoverModule } from 'primeng/popover';
import { StyleClassModule } from 'primeng/styleclass';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import type {
  Class,
  ClassBookingRequest,
  ClassBookingResponse,
  ClassClient,
  Client,
  CompClassRequest,
  MarkClientCheckedinRequest,
  MarkClientNoshowRequest,
  RemoveClientFromClassRequest,
  UpdateClassBooking,
  UpdateMembership,
} from '@fitness-force/models';
import {
  ClassService,
  ClassBookingService,
  ClientService,
  ToastMessageService,
} from '@fitness-force/api';
import { BehaviorSubject } from 'rxjs';
import { ClassStatus, ClientBookingStatus, ClassBookingCancellationType } from '@fitness-force/enum';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { ShortNamePipe } from '@fitness-force/pipes/short-name';
import { MiniSearchComponent, MiniSearchV2Component } from '@fitness-force/common/search';
import { environment } from '@fitness-force/environments';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TagModule } from 'primeng/tag';
import { Router } from '@angular/router';
import { getTrackModeI18nKey } from '../../../../utils/booking-track-mode.util';
import {
  getB2cBookingDisplayStatus,
  getB2cBookingStatusI18nKey,
  getWindowOpenClosedI18nKey,
  isEarlyCancelWindowOpen,
  isWaitlistRosterOpen,
} from '../class-window-status.util';
@Component({
  selector: 'class-client-booking',
  templateUrl: './class-client-booking.component.html',
  styleUrls: ['./class-client-booking.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  providers: [ToastMessageService],
  imports: [
    DialogModule,
    InputTextModule,
    ButtonModule,
    SelectModule,
    DividerModule,
    AvatarModule,
    StyleClassModule,
    FormsModule,
    ReactiveFormsModule,
    MiniSearchComponent,
    MiniSearchV2Component,
    PopoverModule,
    ScrollPanelModule,
    AvatarModule,
    ShortNamePipe,
    TooltipModule,
    DrawerModule,
    RadioButtonModule,
    ProgressSpinnerModule,
    TranslateModule,
    AsyncPipe,
    NgClass,
    SlicePipe,
    TitleCasePipe,
    TagModule,
  ],
})
export class ClassClientBookingComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly clientService = inject(ClientService);
  private readonly translate = inject(TranslateService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly classService = inject(ClassService);
  datePipe = inject(DatePipe);
  readonly miniSearchV2Enabled = environment.miniSearchV2;
  private readonly classBookingService = inject(ClassBookingService);
  private readonly fb = inject(FormBuilder);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly renderer = inject(Renderer2);

  readonly ClassBookingCancellationType = ClassBookingCancellationType;

  readonly tenantId = input<number>(0);
  readonly classObj = input<Class>(undefined, { alias: 'classData' });
  readonly showLeftView = input<boolean>(false);
  readonly hideViewClass = output<boolean>({ alias: 'CloseViewClass' });
  readonly updateClassData = output<any>({ alias: 'UpdateClassData' });

  markCompleted: boolean = false;
  markAllAttended: boolean = false;

  cancelClassPopup: boolean = false;
  deleteClassPopup: boolean = false;
  showAddClientConfirmPopup: boolean = false;
  showRemoveClientCancelTypeDialog = false;
  removeClientCancelTypeLoading = false;
  activeRemoveCancellationType: ClassBookingCancellationType | null = null;
  removeCancelDialogClient: ClassClient | null = null;
  private pendingRemoveClient: ClassClient | null = null;
  private pendingRemoveEvent: Event | null = null;

  showMiniSearch = new BehaviorSubject<boolean>(true);
  selectedClient: Client | any;

  isClassCompletedOrCancelled: boolean = false;
  selectedMemberStatus: { name: string; status: string; count: number } | any;

  memberstatus = [
    { name: 'Booked', status: ClientBookingStatus.Booked.toString(), count: 0 },
    {
      name: 'CheckedIn',
      status: ClientBookingStatus.Attended.toString(),
      count: 0,
    },
    {
      name: 'Cancelled',
      status: ClientBookingStatus.Cancelled.toString(),
      count: 0,
    },
    {
      name: 'Waitlist',
      status: ClientBookingStatus.Waitlist.toString(),
      count: 0,
    },
    { name: 'NoShow', status: ClientBookingStatus.NoShow.toString(), count: 0 },
  ];

  clients$ = new BehaviorSubject<any[]>([]);
  clientBookingResponse: ClassBookingResponse;
  clientMembershipForm: FormGroup;
  sendGroupMsgs: boolean = false;
  showMedicalAlert: boolean = false;
  clientMedicalDetails: any;
  showAddMemberMsg: boolean = false;
  showCreateProspectPopup$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  showLoader$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showCompSessionPopup: boolean;
  complimentoryForm: UntypedFormGroup = new UntypedFormGroup({});
  addToRecurringClassForm: UntypedFormGroup = new UntypedFormGroup({});
  isComplimentoryLoading: boolean = false;

  showRecurringEventUpdatePopup: boolean = false;
  btnLoading: boolean = false;
  isMobileFooterExpanded: boolean = false;

  constructor() {
    effect(() => {
      const classObj = this.classObj();
      if (!classObj) return;

      this.isClassCompletedOrCancelled =
        classObj.status == ClassStatus.Completed.toString() ||
        classObj.status == ClassStatus[ClassStatus.Completed] ||
        classObj.status == ClassStatus.Cancelled.toString() ||
        classObj.status == ClassStatus[ClassStatus.Cancelled];
      this.markCompleted =
        classObj.status == ClassStatus.Completed.toString() ||
        classObj.status == ClassStatus[ClassStatus.Completed];
      this.markAllAttended = this.markCompleted;
      this.cd.markForCheck();
    });
  }

  ngOnInit(): void {
    this.complimentoryForm = this.fb.group({
      comments: ['', [Validators.required]],
      booking_id: [],
    });
    this.addToRecurringClassForm = this.fb.group({
      updateEvent: ['thisClass', [Validators.required]],
    });

    this.clientMembershipForm = this.fb.group({});
    const classObj = this.classObj();
    if (!classObj) {
      return;
    }

    if (classObj.class_clients) {
      if (this.selectedMemberStatus)
        this.clients$.next(
          classObj.class_clients.filter(
            (c) => c.class_booking_status == this.selectedMemberStatus.status,
          ),
        );
      else {
        this.selectedMemberStatus = this.memberstatus.find(
          (m) => m.status == ClientBookingStatus.Booked,
        );
        this.clients$.next(
          classObj.class_clients.filter(
            (c) =>
              c.class_booking_status == this.selectedMemberStatus.status &&
              c.class_booking_is_waitlist == false,
          ),
        );
      }
      this.UpdateForm();
    } else {
      this.selectedMemberStatus = this.memberstatus.find(
        (m) => m.status == ClientBookingStatus.Booked,
      );
    }
  }

  UpdateForm() {
    const classObj = this.classObj();
    if (!classObj?.class_clients) {
      return;
    }
    if (classObj.class_clients) {
      classObj.class_clients.forEach((clt) => {
        if (clt.memberships) {
          clt.selectedMembership =
            clt.memberships.find((o) => o.is_selected) ?? null;
          if (clt.selectedMembership) {
            this.clientMembershipForm.addControl(
              'clientMembership_' + clt.class_booking_client_Id,
              new FormControl(clt.selectedMembership),
            );
          } else {
            this.clientMembershipForm.addControl(
              'clientMembership_' + clt.class_booking_client_Id,
              new FormControl('', []),
            );
          }
        } else {
          this.clientMembershipForm.addControl(
            'clientMembership_' + clt.class_booking_client_Id,
            new FormControl('', []),
          );
        }
      });
      this.clients$.next(
        classObj.class_clients.filter(
          (c) => c.class_booking_status == this.selectedMemberStatus.status,
        ),
      );
    }
  }

  addToRecurringClass(event: boolean) {
    this.btnLoading = true;
    if (event) {
      const clientids: number[] = [];
      clientids.push(this.selectedClient.clt_id);
      const classObj = this.classObj();
      if (!classObj) {
        return;
      }
      if (this.GetSelectedUpdateEventFormControlValue == 'thisClass') {
        this.AddClientToClass(clientids, false).then((res: boolean) => {
          if (res) {
            classObj.total_booking = classObj.total_booking + clientids.length;
            this.handleResponse(this.selectedClient, false);
            this.showMiniSearch.next(true);
            this.showLoader$.next(false);
            this.showRecurringEventUpdatePopup = false;
            this.btnLoading = false;
            this.cd.detectChanges();
          }
        });
      } else {
        this.AddClientToClass(clientids, false).then((res: boolean) => {
          if (res) {
            classObj.total_booking = classObj.total_booking + clientids.length;
            this.handleResponse(this.selectedClient, false);
            this.showMiniSearch.next(true);
            this.showLoader$.next(false);
            this.showRecurringEventUpdatePopup = false;
            this.btnLoading = false;
            this.cd.detectChanges();
          }
        });
      }
    } else {
      this.showRecurringEventUpdatePopup = false;
      this.btnLoading = false;
      this.showMiniSearch.next(true);
      this.selectedClient = null;
      this.showLoader$.next(false);
    }
  }

  AddClient(client: Client) {
    if (client && client.clt_id > 0) {
      this.showMiniSearch.next(false);
      this.showLoader$.next(true);
      this.selectedClient = client;
      const clientids: number[] = [];
      clientids.push(client.clt_id);
      //   if(this.classObj.recurring_id > 0){
      //     this.showMiniSearch.next(true);
      //     this.showLoader$.next(false);
      //     this.showRecurringEventUpdatePopup = true;
      //     return;
      //  }
      const classObj = this.classObj();
      if (!classObj) {
        return;
      }
      if (classObj.total_booking >= classObj.max_capacity) {
        if (classObj.total_waitlist_booking >= classObj.max_waitlist) {
          this.showMiniSearch.next(true);
          this.showLoader$.next(false);
          this.selectedClient = null;
          this.translate
            .get('SCHEDULE.VIEWCLASS.MSG.NO_SLOTS_AVAILABLE')
            .subscribe((translation) => {
              this.toastMessageService.showErrorMessage(translation);
            });
        } else this.showAddClientConfirmPopup = true;
      } else {
        this.AddClientToClass(clientids, false).then((res: boolean) => {
          if (res) {
            classObj.total_booking = classObj.total_booking + clientids.length;
            this.handleResponse(client, false);
            this.showLoader$.next(false);
            this.cd.detectChanges();
          }
        });
      }
    } else {
      this.selectedClient = null;
      this.showLoader$.next(false);
      this.showRecurringEventUpdatePopup = false;
    }
  }

  handleResponse(client: any, isWaitlist: boolean) {
    if (this.clientBookingResponse.client_booked_details != null || undefined) {
      this.clientBookingResponse.client_booked_details.forEach((c) => {
        const classCLients: ClassClient = {} as ClassClient;
        classCLients.class_booking_Id = 0;
        classCLients.class_booking_client_Id = c.client_id;
        classCLients.class_booking_status = ClientBookingStatus.Booked;
        classCLients.full_name = client.clt_full_name;
        classCLients.emailid = client.clt_emailid;
        classCLients.mobile_country_code = client.clt_mobile_country_code;
        classCLients.mobile_no = client.clt_mobile_no;
        classCLients.image = client.clt_image;
        classCLients.class_booking_is_first_time_class = c.is_first_time_class;
        classCLients.medical_alert = c.medical_alert;
        classCLients.memberships = c.memberships;
        classCLients.selectedMembership = c.memberships
          ? (c.memberships.find((m) => m.is_selected) ?? null)
          : null;
        classCLients.class_booking_is_waitlist = isWaitlist;
        classCLients.client_status = client.clt_status;
        const classObj = this.classObj();
        if (!classObj) {
          return;
        }
        if (classObj.class_clients) {
          classObj.class_clients.push(classCLients);
        } else {
          classObj.class_clients = [] as ClassClient[];
          classObj.class_clients.push(classCLients);
        }
      });
    }
    const classObj = this.classObj();
    if (!classObj?.class_clients) {
      return;
    }
    classObj.class_clients.forEach((clt) => {
      if (clt.memberships) {
        clt.selectedMembership =
          clt.memberships.find((o) => o.is_selected) ?? null;
        if (clt.selectedMembership) {
          this.clientMembershipForm.addControl(
            'clientMembership_' + clt.class_booking_client_Id,
            new FormControl(clt.selectedMembership, []),
          );
        } else {
          this.clientMembershipForm.addControl(
            'clientMembership_' + clt.class_booking_client_Id,
            new FormControl('', []),
          );
        }
        this.clientMembershipForm.addControl(
          'clientMembership_' + clt.class_booking_client_Id,
          new FormControl(clt.selectedMembership, []),
        );
      } else {
        this.clientMembershipForm.addControl(
          'clientMembership_' + clt.class_booking_client_Id,
          new FormControl('', []),
        );
      }
    });
    //this.getClients();
    this.showAddClientConfirmPopup = false;
    this.updateClassData.emit(this.classObj());
    this.cd.detectChanges();
  }

  AddClientToClass(
    clientIds: number[],
    addToWaitList: boolean,
    moveWaitlistToBooked: boolean = false,
  ) {
    const classObj = this.classObj();
    if (!classObj) {
      return Promise.resolve(false);
    }
    const classBookingRequest: ClassBookingRequest = {} as ClassBookingRequest;
    classBookingRequest.client_ids = clientIds;
    classBookingRequest.add_to_waitlist = addToWaitList;
    classBookingRequest.move_waitlist_to_booked = moveWaitlistToBooked;
    const promise: Promise<boolean> = new Promise((resolve) => {
      this.classBookingService
        .BookClientsToClass(this.tenantId(), classObj.id, classBookingRequest)
        .subscribe({
          next: (data: ClassBookingResponse) => {
            this.clientBookingResponse = data;
            this.translate
              .get('SCHEDULE.VIEWCLASS.MSG.CLIENT_ADDDED_MSG')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
            this.showMiniSearch.next(true);
            this.showRecurringEventUpdatePopup = false;
            resolve(true);
          },
          error: (error: AppErrors) => {
            this.ShowToastErrors(error);
            this.showMiniSearch.next(true);
            this.showLoader$.next(false);
            this.showRecurringEventUpdatePopup = false;
            resolve(false);
          },
        });
    });
    return promise;
  }

  AddClientToWaitlist() {
    const clientids: number[] = [];
    clientids.push(this.selectedClient.clt_id);
    const classObj = this.classObj();
    if (!classObj) {
      return;
    }
    this.AddClientToClass(clientids, true, false).then((res: boolean) => {
      if (res) {
        classObj.total_waitlist_booking =
          classObj.total_waitlist_booking + clientids.length;
        this.showAddClientConfirmPopup = false;
        this.handleResponse(this.selectedClient, true);
      }
      this.showLoader$.next(false);
      this.showMiniSearch.next(true);
      this.selectedClient = null;
      this.cd.detectChanges();
    });
  }

  MoveWaitlistToBooked(client: ClassClient, event: Event) {
    if (!client.class_booking_is_waitlist) {
      return;
    }
    this.addClass(event);
    const clientids: number[] = [];
    clientids.push(client.class_booking_client_Id);
    const classObj = this.classObj();
    if (!classObj) {
      return;
    }
    this.AddClientToClass(clientids, false, true).then((res: boolean) => {
      if (res) {
        classObj.total_waitlist_booking = classObj.total_waitlist_booking - 1;
        classObj.total_booking = classObj.total_booking + 1;
        if (classObj.class_clients) {
          classObj.class_clients.forEach((cls) => {
            if (
              cls.class_booking_client_Id === client.class_booking_client_Id
            ) {
              cls.class_booking_is_waitlist = false;
            }
          });
        }
        this.updateClassData.emit(classObj);
      }
      this.remoevClass(event);
      this.cd.detectChanges();
    });
  }

  MarkClientNoshow(client: ClassClient, event: Event) {
    if (client.class_booking_is_waitlist) {
      return;
    }
    this.addClass(event);
    const clientNoShowRequest: MarkClientNoshowRequest =
      {} as MarkClientNoshowRequest;
    const clientids: number[] = [];
    clientids.push(client.class_booking_client_Id);
    clientNoShowRequest.client_ids = clientids;
    const classObj = this.classObj();
    if (!classObj) {
      return;
    }
    this.classBookingService
      .MarkNoShow(this.tenantId(), classObj.id, clientNoShowRequest)
      .subscribe({
        next: () => {
          this.translate
            .get('SCHEDULE.VIEWCLASS.MSG.MARK_NOSHOW_MSG')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });

          if (classObj.class_clients) {
            classObj.class_clients.forEach((cls) => {
              if (cls.class_booking_client_Id == client.class_booking_client_Id)
                cls.class_booking_status = ClientBookingStatus.NoShow;
            });
          }
          this.remoevClass(event);
        },
        error: (error: AppErrors) => {
          this.remoevClass(event);
          this.ShowToastErrors(error);
        },
      });
  }

  MarkClientCheckedIn(client: ClassClient, event: Event) {
    if (client.class_booking_is_waitlist) {
      return;
    }
    this.addClass(event);
    const clientCheckedInRequest: MarkClientCheckedinRequest =
      {} as MarkClientCheckedinRequest;
    const clientids: number[] = [];
    clientids.push(client.class_booking_client_Id);
    clientCheckedInRequest.client_ids = clientids;
    const classObj = this.classObj();
    if (!classObj) {
      return;
    }
    this.classBookingService
      .MarkCheckedIn(this.tenantId(), classObj.id, clientCheckedInRequest)
      .subscribe({
        next: () => {
          this.translate
            .get('SCHEDULE.VIEWCLASS.MSG.MARK_CHECKEDIN_MSG')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });

          if (classObj.class_clients) {
            classObj.class_clients.forEach((cls) => {
              if (cls.class_booking_client_Id == client.class_booking_client_Id)
                cls.class_booking_status = ClientBookingStatus.Attended;
            });
            this.clients$.next(classObj.class_clients);
          }
          this.updateClassData.emit(classObj);
          this.remoevClass(event);
        },
        error: (error: AppErrors) => {
          this.remoevClass(event);
          this.ShowToastErrors(error);
        },
      });
  }

  RemoveClientFromClass(client: ClassClient, event: Event) {
    this.addClass(event);
    this.removeClientFromClassApi(
      client,
      event,
      ClassBookingCancellationType.None,
    );
  }

  private removeClientFromClassApi(
    client: ClassClient,
    event: Event,
    cancellationType: ClassBookingCancellationType,
  ): void {
    const classObj = this.classObj();
    if (!classObj) {
      return;
    }

    const clientids = [client.class_booking_client_Id];
    const removeClientFromClass: RemoveClientFromClassRequest = {
      client_ids: clientids,
      cancellation_type: cancellationType,
    };

    this.classBookingService
      .RemoveClientsToClass(this.tenantId(), classObj.id, removeClientFromClass)
      .subscribe({
        next: () => {
          this.translate
            .get('SCHEDULE.VIEWCLASS.MSG.REMOVE_MSG')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });

          if (client.class_booking_is_waitlist) {
            classObj.total_waitlist_booking =
              classObj.total_waitlist_booking - clientids.length;
          } else {
            classObj.total_booking = classObj.total_booking - clientids.length;
          }

          if (classObj.class_clients) {
            classObj.class_clients.forEach((cls) => {
              if (clientids.includes(cls.class_booking_client_Id)) {
                cls.class_booking_status = ClientBookingStatus.Cancelled;
              }
            });

            this.clients$.next(classObj.class_clients);
          }
          this.updateClassData.emit(classObj);
          this.remoevClass(event);
          this.resetRemoveClientCancelTypeDialogState();
        },
        error: (error: AppErrors) => {
          if (
            cancellationType === ClassBookingCancellationType.None &&
            this.isBookingCancelBeforeError(error)
          ) {
            this.openRemoveClientCancelTypeDialog(client, event);
            return;
          }
          this.remoevClass(event);
          this.resetRemoveClientCancelTypeDialogState();
          this.ShowToastErrors(error);
          this.cd.detectChanges();
        },
      });
  }

  private isBookingCancelBeforeError(error: AppErrors): boolean {
    const apiError = error?.originalError?.error;
    if (Array.isArray(apiError)) {
      return apiError.some(
        (e: { errorCode?: string }) =>
          e.errorCode?.toLowerCase() === 'bookingcancelbefore',
      );
    }

    const errorCode = error?.errorCode ?? apiError?.errorCode;
    return errorCode?.toLowerCase() === 'bookingcancelbefore';
  }

  private openRemoveClientCancelTypeDialog(
    client: ClassClient,
    event: Event,
  ): void {
    this.pendingRemoveClient = client;
    this.pendingRemoveEvent = event;
    this.removeCancelDialogClient = client;
    this.activeRemoveCancellationType = null;
    this.removeClientCancelTypeLoading = false;
    this.showRemoveClientCancelTypeDialog = true;
    this.cd.markForCheck();
  }

  closeRemoveClientCancelTypeDialog(): void {
    if (this.removeClientCancelTypeLoading) {
      return;
    }

    const event = this.pendingRemoveEvent;
    this.resetRemoveClientCancelTypeDialogState();
    if (event) {
      this.remoevClass(event);
    }
    this.cd.markForCheck();
  }

  onConfirmRemoveWithCancellationType(
    cancellationType: ClassBookingCancellationType,
  ): void {
    const client = this.pendingRemoveClient;
    const event = this.pendingRemoveEvent;
    if (!client || !event || this.removeClientCancelTypeLoading) {
      return;
    }

    this.activeRemoveCancellationType = cancellationType;
    this.removeClientCancelTypeLoading = true;
    this.cd.markForCheck();
    this.removeClientFromClassApi(client, event, cancellationType);
  }

  private resetRemoveClientCancelTypeDialogState(): void {
    this.showRemoveClientCancelTypeDialog = false;
    this.removeClientCancelTypeLoading = false;
    this.activeRemoveCancellationType = null;
    this.removeCancelDialogClient = null;
    this.pendingRemoveClient = null;
    this.pendingRemoveEvent = null;
  }

  MarkAllClientsAttended(markAllAttended: boolean) {
    const classObj = this.classObj();
    if (!classObj?.class_clients) {
      return;
    }
    let isValid: boolean = false;
    if (classObj.class_clients.length > 0)
      isValid = classObj.class_clients.some(
        (c) =>
          c.class_booking_status.toLowerCase() ==
          ClientBookingStatus.Booked.toLowerCase(),
      );
    if (!markAllAttended && isValid) {
      const clients = classObj.class_clients.filter(
        (c) =>
          c.class_booking_status.toLowerCase() ==
          ClientBookingStatus.Booked.toLowerCase(),
      );
      const clientids = clients.map((s) => {
        return s.class_booking_client_Id;
      });
      const clientCheckedInRequest: MarkClientCheckedinRequest =
        {} as MarkClientCheckedinRequest;
      clientCheckedInRequest.client_ids = clientids;
      this.classBookingService
        .MarkCheckedIn(this.tenantId(), classObj.id, clientCheckedInRequest)
        .subscribe({
          next: () => {
            this.translate
              .get('SCHEDULE.VIEWCLASS.MSG.MARK_CHECKEDIN_MSG')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
            classObj.class_clients.map(
              (c) => (c.class_booking_status = ClientBookingStatus.Attended),
            );
            this.markAllAttended = true;
            this.clients$.next(classObj.class_clients);
            this.updateClassData.emit(classObj);
          },
          error: (error: AppErrors) => {
            this.ShowToastErrors(error);
          },
        });
    } else {
      this.translate
        .get('SCHEDULE.VIEWCLASS.MSG.NO_CLIENTS_TO_MARK_ATTEND')
        .subscribe((translation) => {
          this.toastMessageService.showSuccess(translation);
        });
    }
  }

  MarkAsCompSession(client: ClassClient, event: Event) {
    this.selectedClient = client;
    this.showCompSessionPopup = true;
  }

  closeCompSessionPopup() {
    this.showCompSessionPopup = false;
    this.selectedClient = null;
    this.complimentoryForm.reset();
  }

  ComplimentoryClass(form: any) {
    this.isComplimentoryLoading = true;
    const compClassRequest: CompClassRequest = {} as CompClassRequest;
    compClassRequest.comment = form.value.comments;
    this.classBookingService
      .MarkComplimentoryClass(
        this.tenantId(),
        this.selectedClient.class_booking_client_Id,
        this.selectedClient.class_booking_Id,
        compClassRequest,
      )
      .subscribe({
        next: async () => {
          this.isComplimentoryLoading = false;
          this.translate
            .get('CLIENT_BOOKING.COMP_COMPLETED')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
          this.showCompSessionPopup = false;
          this.selectedClient = null;
          this.cd.detectChanges();
        },
        error: (error: AppErrors) => {
          this.isComplimentoryLoading = false;
          this.ShowToastErrors(error);
          this.showCompSessionPopup = false;
          this.selectedClient = null;
          this.cd.detectChanges();
        },
      });
  }

  MarkClassComplete(markCompleted: boolean) {
    const classObj = this.classObj();
    if (!classObj) {
      return;
    }
    if (!markCompleted) {
      this.classService
        .MarkClassComplete(this.tenantId(), classObj.id)
        .subscribe({
          next: () => {
            this.translate
              .get('SCHEDULE.VIEWCLASS.MSG.COMPLETE_MSG')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
            this.markCompleted = true;
            classObj.status = ClassStatus.Completed;
            this.updateClassData.emit(classObj);
          },
          error: (error: AppErrors) => {
            this.ShowToastErrors(error);
            this.deleteClassPopup = false;
          },
        });
    }
  }

  ShowToastErrors(error: any) {
    this.toastMessageService.showError(error);
  }

  ClosePopup() {
    this.hideViewClass.emit(true);
  }

  ToggleButton(status: any) {
    this.selectedMemberStatus = status;
    //this.getClients();
  }

  filterClients(event: any) {
    this.ToggleButton(event.value);
  }

  ChangeMembership(event: any, client: ClassClient) {
    const updateRequest: UpdateClassBooking = {} as UpdateClassBooking;
    updateRequest.clients_with_memberships = [] as UpdateMembership[];
    const updateClientMembership: UpdateMembership = {} as UpdateMembership;
    updateClientMembership.client_id = client.class_booking_client_Id;
    updateClientMembership.subscription_id = event.value.subscription_id;
    updateClientMembership.membership_id = event.value.id;
    updateRequest.clients_with_memberships.push(updateClientMembership);
    const classObj = this.classObj();
    if (!classObj) {
      return;
    }
    this.classBookingService
      .UpdateClientmembership(this.tenantId(), classObj.id, updateRequest)
      .subscribe({
        next: (data: ClassBookingResponse) => {
          this.clientBookingResponse = data;
          this.translate
            .get('SCHEDULE.VIEWCLASS.MSG.UPDATE_MEMBERSHIP_MSG')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
          this.updateClassData.emit(classObj);
        },
        error: (error: AppErrors) => {
          this.ShowToastErrors(error);
        },
      });
  }

  getClientMembership(clientId: number) {
    return this.clientMembershipForm.get('clientMembership_' + clientId)?.value;
  }

  get getClients() {
    const classObj = this.classObj();
    if (!classObj?.class_clients) {
      return [];
    }
    if (classObj.class_clients) {
      let filterClients = [];
      if (
        this.selectedMemberStatus &&
        this.selectedMemberStatus.status ==
          ClientBookingStatus.Waitlist.toString()
      )
        filterClients = classObj.class_clients.filter(
          (c) =>
            c.class_booking_is_waitlist == true &&
            c.class_booking_status == ClientBookingStatus.Booked,
        );
      else if (
        this.selectedMemberStatus &&
        this.selectedMemberStatus.status !=
          ClientBookingStatus.Waitlist.toString() &&
        this.selectedMemberStatus.status !=
          ClientBookingStatus.Booked.toString()
      ) {
        filterClients = classObj.class_clients.filter(
          (c) => c.class_booking_status == this.selectedMemberStatus.status,
        );
      } else {
        this.selectedMemberStatus = this.memberstatus.find(
          (m) => m.status == ClientBookingStatus.Booked,
        );
        filterClients = classObj.class_clients.filter(
          (c) =>
            c.class_booking_status == this.selectedMemberStatus.status &&
            c.class_booking_is_waitlist == false,
        );
      }
      filterClients.length == 0
        ? (this.showAddMemberMsg = true)
        : (this.showAddMemberMsg = false);
      this.clients$.next(filterClients);
      this.showAddMemberMsg = false;
      return this.clients$.value;
    } else {
      this.showAddMemberMsg = true;
      return [];
    }
  }

  mapClientForMemberDisplay(client: ClassClient) {
    return {
      id: client.class_booking_client_Id,
      full_name: client.full_name,
      image: client.image,
      client_status: client.client_status,
      mobile_country_code: client.mobile_country_code,
      mobile_number: client.mobile_no ?? (client as ClassClient & { mobile_number?: number }).mobile_number,
      email_id: client.emailid,
    };
  }

  openClientProfile(memberRow: { id?: number }) {
    if (memberRow?.id) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/client/profile/', memberRow.id]),
      );
      window.open(url, '_blank');
    }
  }

  getClientType(row: { client_status?: string | null }): string {
    switch (row?.client_status) {
      case 'Client':
        return 'Member';
      default:
        return '';
    }
  }

  clientMemberStatus(client: { client_status?: string | null }): string {
    if (client?.client_status) {
      return this.clientService.getClientStatusHtml(
        client.client_status.toString(),
      );
    }
    return '';
  }

  GetClietMedicalDetails(client: any) {
    this.clientMedicalDetails = client;
    this.showMedicalAlert = true;
    this.cd.detectChanges();
  }

  closeMedicalAlert() {
    this.showMedicalAlert = false;
    this.clientMedicalDetails = null;
    this.cd.detectChanges();
  }

  timeout(ms: any) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  get clientBookingStatus() {
    return ClientBookingStatus;
  }

  get GetMemberStatus() {
    const classObj = this.classObj();
    if (!classObj?.class_clients) {
      return this.memberstatus;
    }
    if (classObj.class_clients) {
      this.memberstatus.filter((s) => {
        if (s.status == ClientBookingStatus.Waitlist.toString()) {
          s.count = classObj.class_clients.filter(
            (c) =>
              c.class_booking_is_waitlist &&
              c.class_booking_status == ClientBookingStatus.Booked,
          ).length;
        } else if (s.status == ClientBookingStatus.Booked.toString()) {
          s.count = classObj.class_clients.filter(
            (c) =>
              c.class_booking_status == s.status &&
              c.class_booking_is_waitlist == false,
          ).length;
        } else {
          s.count = classObj.class_clients.filter(
            (c) => c.class_booking_status == s.status,
          ).length;
        }
      });
      return this.memberstatus;
    } else return this.memberstatus;
  }

  createProspect(event: any) {
    this.showCreateProspectPopup$.next(true);
  }

  miniSearchV2DialogSubtitle(): string {
    const c = this.classObj();
    if (!c) {
      return '';
    }
    const when = this.datePipe.transform(c.start_datetime, 'medium') ?? '';
    return [c.type_name_primary_language, when, c.coach_name].filter(Boolean).join(' · ');
  }

  hidePopup(clientId: number) {
    this.showCreateProspectPopup$.next(false);
  }

  addClass(event: Event) {
    const clickedElement = event.target as HTMLElement;
    // Find the icon element - either it's a child or the clicked element itself or in the parent
    let iconElement: Element | null = null;

    // Check if clicked element has the icon class
    if (clickedElement.classList.contains('fa')) {
      iconElement = clickedElement;
    }
    // Check if first child is an icon
    else if (clickedElement.firstElementChild?.classList.contains('fa')) {
      iconElement = clickedElement.firstElementChild;
    }
    // Check if parent has icon as first child (in case text was clicked)
    else if (clickedElement.parentElement?.querySelector('.fa')) {
      iconElement = clickedElement.parentElement.querySelector('.fa');
    }

    if (iconElement) {
      const classesToAdd = ['fa-spin', 'fa-spinner'];
      classesToAdd.forEach((className) => {
        this.renderer.addClass(iconElement, className);
      });
    }
  }

  remoevClass(event: Event) {
    const clickedElement = event.target as HTMLElement;
    // Find the icon element - either it's a child or the clicked element itself or in the parent
    let iconElement: Element | null = null;

    // Check if clicked element has the icon class
    if (clickedElement.classList.contains('fa')) {
      iconElement = clickedElement;
    }
    // Check if first child is an icon
    else if (clickedElement.firstElementChild?.classList.contains('fa')) {
      iconElement = clickedElement.firstElementChild;
    }
    // Check if parent has icon as first child (in case text was clicked)
    else if (clickedElement.parentElement?.querySelector('.fa')) {
      iconElement = clickedElement.parentElement.querySelector('.fa');
    }

    if (iconElement) {
      const classesToAdd = ['fa-spin', 'fa-spinner'];
      classesToAdd.forEach((className) => {
        this.renderer.removeClass(iconElement, className);
      });
    }
  }

  get GetSelectedUpdateEventFormControlValue(): any {
    return this.addToRecurringClassForm.get('updateEvent')?.value;
  }

  get earlyCancelWindowOpen(): boolean {
    const classObj = this.classObj();
    if (!classObj?.start_datetime) {
      return false;
    }
    return isEarlyCancelWindowOpen(classObj);
  }

  get waitlistRosterOpen(): boolean {
    const classObj = this.classObj();
    if (!classObj?.start_datetime) {
      return false;
    }
    return isWaitlistRosterOpen(classObj);
  }

  get earlyCancelWindowStatusKey(): string {
    return getWindowOpenClosedI18nKey(this.earlyCancelWindowOpen);
  }

  get waitlistRosterStatusKey(): string {
    return getWindowOpenClosedI18nKey(this.waitlistRosterOpen);
  }

  get b2cBookingStatusKey(): string {
    const classObj = this.classObj();
    if (!classObj?.start_datetime) {
      return getB2cBookingStatusI18nKey('closed');
    }
    return getB2cBookingStatusI18nKey(getB2cBookingDisplayStatus(classObj));
  }

  get b2cBookingTagSeverity(): 'success' | 'info' | 'secondary' | 'warn' {
    const classObj = this.classObj();
    if (!classObj?.start_datetime) {
      return 'warn';
    }
    switch (getB2cBookingDisplayStatus(classObj)) {
      case 'accepts':
        return 'success';
      case 'not_opened':
        return 'secondary';
      case 'closed':
        return 'warn';
    }
  }

  isMemberStatusTabSelected(status: {
    name: string;
    status: string;
    count: number;
  }): boolean {
    return (
      this.selectedMemberStatus !== null &&
      this.selectedMemberStatus === status
    );
  }

  isTrialBooking(client: ClassClient): boolean {
    return (client.trial_id ?? 0) > 0;
  }

  trackModeLabel(client: ClassClient): string {
    return getTrackModeI18nKey(client.created_track_mode);
  }

  hasSubscription(client: ClassClient): boolean {
    return (
      (client.memberships?.length ?? 0) > 0 || !!client.selectedMembership
    );
  }

  showSubscriptionDropdown(client: ClassClient): boolean {
    return (
      (client.memberships?.length ?? 0) > 1 &&
      client.class_booking_status !== 'Cancelled'
    );
  }
}
